import { Stack, Tabs, Tab } from '@mui/material';
import React from 'react';
import {
  useParams,
  useLocation,
  Switch,
  Route,
  Redirect,
  Link as RouterLink,
} from 'react-router-dom';

import { ExtractParams } from '@/types/params';

import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import { useGetOneUnit } from '@/api/endpoints/unit/useGetOneUnit';
import { PMPRoutes } from '@/config/routes';
import { getResolvedRoutePath } from '@/utils/router';

import UnitViewDocumentListSubpage from './unit-view/UnitViewDocumentListSubpage';
import UnitViewMessageListSubpage from './unit-view/UnitViewMessageListSubpage';
import UnitViewPastTenantListSubpage from './unit-view/UnitViewPastTenantListSubpage';
import UnitViewRentSubpage from './unit-view/UnitViewRentSubpage';
import UnitViewTenantListSubpage from './unit-view/UnitViewTenantListSubpage';

const UnitViewPage: React.FC = () => {
  const { id } = useParams<ExtractParams<PMPRoutes.unitView>>();
  const location = useLocation();

  const getOneUnitQuery = useGetOneUnit(Number(id));

  const navigationItems = [
    {
      label: 'Tenants',
      path: getResolvedRoutePath(PMPRoutes.unitViewTenantList, { id }),
    },
    {
      label: 'Rent Management',
      path: getResolvedRoutePath(PMPRoutes.unitViewRent, { id }),
    },
    {
      label: 'Messages',
      path: getResolvedRoutePath(PMPRoutes.unitViewMessageList, { id }),
    },
    {
      label: 'Documents',
      path: getResolvedRoutePath(PMPRoutes.unitViewDocumentList, { id }),
    },
    {
      label: 'Past Tenants',
      path: getResolvedRoutePath(PMPRoutes.unitViewPastTenantList, { id }),
    },
  ];
  
  const activeTabPath =
  navigationItems.find(item => location.pathname.startsWith(item.path))?.path || '';


  return (
    <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%', px: 0 }}>
      <DashboardHeader
        breadcrumbs={[
          { label: 'Buildings', to: PMPRoutes.buildingList },
          {
            label: getOneUnitQuery.data?.building?.name || 'Unnamed',
            to: getResolvedRoutePath(PMPRoutes.buildingView, {
              id: String(getOneUnitQuery.data?.building_id),
            }),
          },
        ]}
        title={getOneUnitQuery.data?.name ? `Unit ${getOneUnitQuery.data?.name}` : undefined}
      />

      <Tabs
        value={activeTabPath}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            textTransform: 'none',
            minWidth: 0,
            px: 2,
          },
        }}
      >
        {navigationItems.map(item => (
          <Tab
            key={item.path}
            value={item.path}
            label={item.label}
            component={RouterLink}
            to={item.path}
          />
        ))}
      </Tabs>

      <Switch>
        <Route exact path={PMPRoutes.unitViewMessageList} component={UnitViewMessageListSubpage} />
        <Route exact path={PMPRoutes.unitViewMessageView} component={UnitViewMessageListSubpage} />
        <Route exact path={PMPRoutes.unitViewTenantList} component={UnitViewTenantListSubpage} />
        <Route
          exact
          path={PMPRoutes.unitViewPastTenantList}
          component={UnitViewPastTenantListSubpage}
        />
        <Route
          exact
          path={PMPRoutes.unitViewDocumentList}
          component={UnitViewDocumentListSubpage}
        />
        {/* Add Route for Rent Subpage */}
        <Route exact path={PMPRoutes.unitViewRent} component={UnitViewRentSubpage} />
        <Route>
          {/* Keep redirecting to TenantList by default */}
          <Redirect to={getResolvedRoutePath(PMPRoutes.unitViewTenantList, { id })} />
        </Route>
      </Switch>
    </Stack>
  );
};

export default UnitViewPage;
